
import Vue from 'vue';
import Component from 'vue-class-component';
import {Getter, Action} from 'vuex-class';
import {AppState, UyapGirisAsamasi, UyapStatusMessage} from '@/types';

interface PrimaryActionConfig {
  label: string;
  icon: string;
  color: string;
  outlined: boolean;
}

interface GirisAdimi {
  key: UyapGirisAsamasi;
  label: string;
  icon: string;
  aktif: boolean;
  bitti: boolean;
}

// Giriş adımlarının sırası. statusDescription ve adım listesi aynı kaynaktan
// beslensin diye tek yerde duruyor.
const GIRIS_ADIMLARI: Array<{ key: UyapGirisAsamasi; label: string }> = [
  {key: 'oturum-kontrol', label: 'Mevcut oturum kontrol ediliyor'},
  {key: 'imza', label: 'E-imza ile giriş yapılıyor'},
  {key: 'bilgiler', label: 'Avukat bilgileri alınıyor'}
];

@Component({})
export default class UyapStatusCard extends Vue {
  @Getter isUyapConnected!: boolean;
  @Getter isUyapConnecting!: boolean;
  @Getter uyapGirisAsamasi!: UyapGirisAsamasi | null;
  @Getter uyapStatusMessage!: UyapStatusMessage | null;
  @Getter healthCheckRetryCount!: number;
  @Getter isHealthChecking!: boolean;
  @Getter availableCardList!: any[];
  @Action connectToUyap!: () => Promise<void>;
  @Action checkUyapSession!: () => Promise<void>;

  isRefreshing = false;
  private readonly defaultHealthRetryLimit = 3;

  get hasAvailableCard(): boolean {
    return Array.isArray(this.availableCardList) && this.availableCardList.length > 0;
  }

  // Kart yokken "Bağlan" tıklanamaz; bağlıyken buton "Yenile" olduğu için etkilenmez.
  get isPrimaryDisabled(): boolean {
    return !this.isUyapConnected && !this.hasAvailableCard;
  }

  get girisAdimlari(): GirisAdimi[] {
    const aktifIndex = GIRIS_ADIMLARI.findIndex(adim => adim.key === this.uyapGirisAsamasi);
    return GIRIS_ADIMLARI.map((adim, index) => {
      const bitti = aktifIndex >= 0 && index < aktifIndex;
      const aktif = index === aktifIndex;
      return {
        key: adim.key,
        label: adim.label,
        aktif,
        bitti,
        icon: bitti ? 'mdi-check-circle' : (aktif ? 'mdi-circle-slice-5' : 'mdi-circle-outline')
      };
    });
  }

  get statusLabel(): string {
    if (this.isUyapConnecting) return 'Bağlanıyor';
    const type = this.uyapStatusMessage?.type;
    if (type === 'info') return 'Kontrol Ediliyor';
    return this.isUyapConnected ? 'Bağlı' : 'Bağlı Değil';
  }

  get statusIcon(): string {
    if (this.isUyapConnecting) return 'mdi-progress-clock';
    const type = this.uyapStatusMessage?.type;
    if (type === 'info') return 'mdi-progress-clock';
    return this.isUyapConnected ? 'mdi-check-circle' : 'mdi-alert-circle';
  }

  get statusColor(): string {
    // eicrapro turuncusu (App.vue .eup-bg-orange ile aynı). Geçiş durumu hem
    // "bağlı"nın yeşilinden hem "değil"in kırmızısından ayrı, hem de markanın rengi.
    if (this.isUyapConnecting) return '#E76100';
    const type = this.uyapStatusMessage?.type;
    if (type === 'error') return '#ef5350';
    if (type === 'success' || this.isUyapConnected) return 'success darken-2';
    if (type === 'info') return '#E1893F';
    return 'error darken-2';
  }

  get statusIconColor(): string {
    if (this.isUyapConnecting) return '#ffffff';
    const type = this.uyapStatusMessage?.type;
    if (type === 'success' || this.isUyapConnected) return '#4caf50';
    if (type === 'error') return '#ff5252';
    if (type === 'info') return '#f59e0b';
    return '#facc15';
  }

  get statusIconBubbleColor(): string {
    if (this.isUyapConnecting) return 'rgba(255, 255, 255, 0.2)';
    const type = this.uyapStatusMessage?.type;
    if (type === 'success' || this.isUyapConnected) return 'rgba(76, 175, 80, 0.15)';
    if (type === 'error') return 'rgba(244, 67, 54, 0.15)';
    if (type === 'info') return 'rgba(245, 158, 11, 0.15)';
    return 'rgba(250, 204, 21, 0.15)';
  }

  get statusTextColor(): string {
    if (this.isUyapConnecting) return 'white';
    const type = this.uyapStatusMessage?.type;
    if (type === 'info') return 'black';
    return 'white';
  }

  get statusDescription(): string {
    // Bağlanırken bayat mesaj gösterilmez: önceki denemenin hata metni,
    // sürmekte olan girişin üstünde "başarısız" izlenimi bırakıyordu.
    if (this.isUyapConnecting) {
      return 'UYAP oturumu açılıyor, lütfen bekleyin.';
    }
    const message = this.uyapStatusMessage?.text;
    if (!message) {
      if (this.isUyapConnected) {
        return 'UYAP bağlantısı aktif ve hazır.';
      }
      if (!this.hasAvailableCard) {
        return 'E-imza kartı takılı değil. Kartı taktığınızda bağlantı kendiliğinden kurulur.';
      }
      return 'Giriş yapılmadı veya oturum süresi doldu.';
    }
    return this.normalizeMessage(message);
  }

  private normalizeMessage(raw: string): string {
    let value = raw.trim();
    value = value.replace(/^error:\s*/i, '');
    if (!value) return 'UYAP bağlantısı başarısız.';

    const lower = value.toLowerCase();
    if (lower.includes('kart') || lower.includes('takılı') || lower.includes('takili')) {
      return 'İmzacı takılı değil.';
    }
    if (lower.includes('timeout') || lower.includes('zaman')) {
      return 'UYAP cevap vermedi, lütfen tekrar deneyin.';
    }
    if (lower.includes('uyap bağlantısı yapılamadı')) {
      return 'UYAP bağlantısı yapılamadı. Lütfen tekrar deneyin.';
    }
    if (lower.includes('bekleyen') && lower.includes('giriş')) {
      return "UYAP'ta bekleyen bir giriş isteği bulunuyor. Tarayıcıdan isteği onaylayın veya iptal edin.";
    }
    return value;
  }

  get maxHealthCheckRetries(): number {
    const limit = (this.$store.state as AppState).healthCheckMaxRetries;
    return typeof limit === 'number' ? limit : this.defaultHealthRetryLimit;
  }

  get heartbeatIcon(): string {
    return this.isHealthChecking ? 'mdi-pulse' : 'mdi-heart-off';
  }

  get heartbeatIconColor(): string {
    // Turuncu zeminde yeşil/amber ikon kayboluyor; bağlanırken beyaz kalıyor.
    if (this.isUyapConnecting) return 'white';
    return this.isHealthChecking ? 'green darken-1' : 'amber darken-2';
  }

  get heartbeatLabel(): string {
    return this.isHealthChecking ? 'Otomatik izleme aktif' : 'Otomatik izleme kapalı';
  }

  get primaryAction(): PrimaryActionConfig {
    if (this.isUyapConnecting) {
      return {
        label: 'Bağlanıyor',
        icon: 'mdi-progress-clock',
        // Turuncu zeminde beyaz outline düğme okunur kalıyor.
        color: 'white',
        outlined: true,
      };
    }
    if (this.isUyapConnected) {
      return {
        label: 'Yenile',
        icon: 'mdi-refresh',
        color: 'grey darken-3',
        outlined: true,
      };
    }

    return {
      label: 'Bağlan',
      icon: 'mdi-link-variant',
      color: 'success darken-1',
      outlined: false,
    };
  }

  // Time: O(1), Space: O(1) — tek aksiyon tetikleyicisi.
  async handlePrimaryAction() {
    if (this.isRefreshing) return;
    this.isRefreshing = true;
    try {
      if (this.isUyapConnected) {
        await this.checkUyapSession();
      } else {
        await this.connectToUyap();
      }
    } catch (error) {
      console.error('UYAP bağlantı işlemi başarısız.', error);
      this.$toast?.error?.('UYAP bağlantısı sağlanamadı. Lütfen tekrar deneyin.');
    } finally {
      this.isRefreshing = false;
    }
  }
}
